import { Food, FoodHeader, FoodSearchTier, FoodSource, getFoodSource } from '../data-model/food';
import { analyzeFoodText } from './food-search-match';
import { FoodWordCompleter } from './food-word-completer';

/**
 * The scanner pick rule (#5590): which of the foods a search found may stand for a food name the
 * AI returned, and in what order.
 *
 * It lives in `shared/` since #5623, because two callers need the same order. The phone takes the
 * first candidate (`findRankedFoodMatchAsync` in `client/src/services/scanned-food-match.ts`). The
 * server shows the first few to the AI model and lets it choose. The searches themselves stay with
 * each caller. The rule and its measurements are in `design-docs/meal-photo-scanner.md`,
 * "Food Matching Logic".
 */

/** One completer for every scan; it holds only the word list and the analyzer. */
export const scanWordCompleter = new FoodWordCompleter();

/**
 * Words that say how a food is prepared or cut rather than what it is. A scanned name must match
 * its food words; these only help choose between candidates that do (#5590).
 */
const COOKED_WORDS = 'cooked boiled baked grilled fried roasted steamed sauteed sautéed stewed poached broiled scrambled toasted braised smoked';
const PREPARATION_WORDS = `${COOKED_WORDS} raw fresh diced sliced slices chopped shredded crushed mashed minced pieces piece strips wedges cubes cubed drained plain whole halved peeled crumbled dried`;

// the words as the matcher sees them (stemmed and folded), built on first use
let cookedTerms: Set<string> | undefined;
let preparationTerms: Set<string> | undefined;

// words that name a food although they stem like a preparation word: "fries" and "fried" both
// become "fri", "toast" and "toasted" both "toast", so "french fries", "toast with jam", "pot roast"
// and "pork chops" must keep them as food words
const FOOD_WORDS_THAT_LOOK_PREPARED = new Set(['fries', 'fry', 'toast', 'toasts', 'roast', 'roasts', 'chop', 'chops']);

function isCookedTerm(term: string): boolean {
  cookedTerms ??= new Set(analyzeFoodText(COOKED_WORDS, scanWordCompleter.analyzerDeps));
  return cookedTerms.has(term);
}

// cooking words written as one hyphenated word. Not in COOKED_WORDS, because the matcher splits them
// and "pan" or "stir" alone would then count as cooking words ("Pan dulce", "pan pizza")
const HYPHENATED_COOKING_WORDS = new Set(['pan-fried', 'stir-fried', 'deep-fried']);

function isPreparationWord(word: string): boolean {
  if (FOOD_WORDS_THAT_LOOK_PREPARED.has(word)) {
    return false;
  }
  if (HYPHENATED_COOKING_WORDS.has(word)) {
    return true;
  }
  preparationTerms ??= new Set(analyzeFoodText(PREPARATION_WORDS, scanWordCompleter.analyzerDeps));
  const terms = preparationTerms;
  return analyzeFoodText(word, scanWordCompleter.analyzerDeps).every(term => terms.has(term));
}

/**
 * The words of a scanned name that say what the food is: the part before "with", "in" or "or",
 * without preparation words. "cooked cassava" gives "cassava"; "chicken in red sauce" gives
 * "chicken". Empty when the name is nothing but preparation words.
 */
export function scannedFoodWords(aiName: string): string {
  return scannedNameHead(aiName).split(/[\s,]+/).filter(word => word && !isPreparationWord(word)).join(' ');
}

/** a scanned name up to "with", "in" or "or", lowercased: the part that names the food itself */
function scannedNameHead(aiName: string): string {
  return aiName.toLowerCase().split(/\s+(?:with|in|or)\s+/)[0];
}

/** every word of a food's names, as the matcher sees them; a staple's aliases count as names */
function foodTerms(food: FoodHeader): string[] {
  const names = [food.name];
  if (getFoodSource(food._id) === FoodSource.STP) {
    names.push(...(food.altNames ?? (food.cachedFood as Food)?.altNames ?? []));
  }
  return names.flatMap(name => analyzeFoodText(name, scanWordCompleter.analyzerDeps));
}

/**
 * How much a food's source is trusted for an automatic pick: the user's own and recent foods and
 * staples most, other trusted databases a little, brand products not at all, Open Food Facts less.
 */
function sourceCredit(food: FoodHeader): number {
  const tier = food.tier ?? FoodSearchTier.Contributed;
  if (tier <= FoodSearchTier.Staples) {
    return 1;
  }
  if (tier === FoodSearchTier.Trusted) {
    // branded products sit in the trusted tier, but a plate holds a generic food
    return getFoodSource(food._id) === FoodSource.FDC ? 0 : 0.5;
  }
  return tier === FoodSearchTier.OpenFoodFacts ? -0.5 : 0;
}

/**
 * Every food that may stand for the AI's name, best first; empty when no candidate names the food.
 *
 * - `aiName` is the name as the AI returned it, trimmed.
 * - `queryTerms` is `completedQuery.terms` of the caller's `searchTextQuery` for that name. It is
 *   passed in, not worked out again here, because the word completer can correct a word, and the
 *   cooked and raw checks below must read the same words the ranking did.
 * - `rankedHeaders` is the caller's search results after `rankFoodHeaders` (source tiers on), which
 *   supplies each food's exact word count, extra word count and tier, and the tie order.
 *
 * The rule:
 * - A candidate must contain every food word of the name exactly (one may be missing when there are
 *   3 or more). Preparation words are never required. Exact only, because the AI makes no typos:
 *   the Foods page's letter-tolerance let "salmon" reach "almond".
 * - Score: the exactly answered words (preparation words included), plus the source credit, minus a
 *   quarter per word of its name the search did not ask for (at most 6), minus 1 when the name wants
 *   it cooked and the food is raw (or the other way round), plus a half when both are cooked.
 * - Highest score first. The sort is stable, so a tie keeps the order of `rankedHeaders`, which is
 *   the Foods page's order.
 */
export function rankScannedFoodCandidates(aiName: string, queryTerms: string[], rankedHeaders: FoodHeader[]): FoodHeader[] {
  const foodWords = scannedFoodWords(aiName);
  const foodTermsOfName = analyzeFoodText(foodWords, scanWordCompleter.analyzerDeps);
  // if the name is nothing but preparation words, those are all it has to go on -- but still only
  // the part before "with", so a side or a sauce is never required
  const required = foodTermsOfName.length > 0
    ? foodTermsOfName
    : analyzeFoodText(scannedNameHead(aiName), scanWordCompleter.analyzerDeps);
  const allowedMissing = required.length >= 3 ? 1 : 0;
  const wantsCooked = queryTerms.some(isCookedTerm);
  const wantsRaw = queryTerms.includes('raw');

  const scored: { food: FoodHeader; score: number }[] = [];
  for (const food of rankedHeaders) {
    const terms = foodTerms(food);
    // if the food does not name what the AI saw, it cannot be the pick
    if (required.filter(term => !terms.includes(term)).length > allowedMissing) {
      continue;
    }
    const isRaw = terms.includes('raw') && !terms.some(isCookedTerm);
    const isCooked = terms.some(isCookedTerm) && !terms.includes('raw');
    let score = (food.exactWords ?? 0) + sourceCredit(food) - 0.25 * Math.min(food.extraWords ?? 0, 6);
    if (wantsCooked && isRaw) {
      score -= 1;
    }
    if (wantsCooked && isCooked) {
      score += 0.5;
    }
    if (wantsRaw && isCooked) {
      score -= 1;
    }
    scored.push({ food, score });
  }
  // Array.prototype.sort is stable, so equal scores stay in the caller's order
  return scored.sort((a, b) => b.score - a.score).map(entry => entry.food);
}
